import { useEffect, useState } from 'react'
import { api } from './api'
import {
  availableAudiences,
  beginSignIn,
  completeSignIn,
  isHostedSignInConfigured,
  loadIdentity,
  personaFromQuery,
  saveIdentity,
  type Identity,
} from './identity'
import { useResource, type Resource } from './useResource'
import type { Appointment, Service } from './types'

const DEV_PERSONAS = [
  { label: "Alice — customer at Joe's Auto", value: "joes-auto/alice@example.com" },
  { label: "Priya — front desk at Joe's Auto", value: "joes-auto/priya@joesauto.example.com" },
  { label: "Joe — owner of Joe's Auto", value: "joes-auto/joe@joesauto.example.com" },
  { label: 'Sam — owner of Rival Motors', value: 'rival-motors/sam@rivalmotors.example.com' },
]

function Panel<T>({
  title,
  note,
  resource,
  children,
}: {
  title: string
  note: string
  resource: Resource<T>
  children: (value: T) => React.ReactNode
}) {
  return (
    <section className="panel">
      <header>
        <h2>{title}</h2>
        <p className="note">{note}</p>
      </header>
      {resource.state === 'loading' && <p className="muted">Loading…</p>}
      {resource.state === 'failed' && (
        <p className="failed">
          <code>{resource.code}</code> {resource.message}
        </p>
      )}
      {resource.state === 'ready' && children(resource.value)}
    </section>
  )
}

function Money({ price }: { price: string | null }) {
  // A price that has not been set is not zero, and rendering it as "$0.00" would be a quote the
  // shop never gave. Prices come from `services`; nothing on this screen invents one.
  if (price === null) return <span className="muted">quoted after inspection</span>
  return <span className="price">${price}</span>
}

function When({ iso, timezone }: { iso: string; timezone: string }) {
  return (
    <time dateTime={iso}>
      {new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone,
      }).format(new Date(iso))}
    </time>
  )
}

type Said = { who: 'you' | 'assistant'; text: string }

/**
 * The same assistant the telephone reaches, in a box.
 *
 * This is a window onto the product, not a second product. A garage's customers will not sign
 * in here — they ring the shop, or use whatever the shop already gives them. What this is for
 * is the owner: seeing the conversation a caller has, the refusals it makes and the booking it
 * writes, without picking up a phone. Every rule met here is the one the agent enforces, and
 * the reply comes back over the same AgentCore runtime a call uses.
 */
function Conversation({ identity }: { identity: Identity }) {
  const [said, setSaid] = useState<Said[]>([])
  const [draft, setDraft] = useState('')
  const [waiting, setWaiting] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  async function send(event: React.FormEvent) {
    event.preventDefault()
    const text = draft.trim()
    if (!text || waiting) return
    setDraft('')
    setFailure(null)
    setSaid((before) => [...before, { who: 'you', text }])
    setWaiting(true)
    try {
      const reply = await api.chat(identity, text)
      // The agent answers with its own envelope: `ok` false carries a refusal, which is a real
      // answer and belongs in the transcript rather than in an error banner.
      const spoken = reply.reply?.trim() || reply.message?.trim() || '…'
      setSaid((before) => [...before, { who: 'assistant', text: spoken }])
    } catch (problem) {
      setFailure(problem instanceof Error ? problem.message : String(problem))
    } finally {
      setWaiting(false)
    }
  }

  return (
    <section className="panel">
      <header>
        <h2>Talk to the assistant</h2>
        <p className="note">
          The same assistant the shop&apos;s phone number reaches, over the same runtime. It sees
          you as whoever you are signed in as — sign in as a customer to hear what a caller
          hears.
        </p>
      </header>
      <ol className="conversation">
        {said.map((line, index) => (
          <li key={index} className={line.who}>
            <span className="who">{line.who === 'you' ? 'You' : 'Assistant'}</span>
            <p>{line.text}</p>
          </li>
        ))}
        {waiting && <li className="assistant muted"><p>…</p></li>}
      </ol>
      {failure && (
        <p className="failed" role="alert">
          {failure}
        </p>
      )}
      <form onSubmit={(e) => void send(e)} className="ask">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="How much is an oil change?"
          aria-label="Say something to the assistant"
          maxLength={2000}
        />
        <button className="button" type="submit" disabled={waiting || !draft.trim()}>
          Send
        </button>
      </form>
    </section>
  )
}

export default function App() {
  const [identity, setIdentity] = useState<Identity | null>(() => personaFromQuery() ?? loadIdentity())
  // Why the last sign-in did not work. Sign-in is the one flow a user cannot inspect for
  // themselves — a failure that only writes to the console is a failure they cannot report.
  const [signInError, setSignInError] = useState<string | null>(null)
  const hostedSignIn = isHostedSignInConfigured()

  useEffect(() => {
    // Exchanging the authorization code is a network call, so unlike reading a token out of
    // the URL fragment this cannot be done synchronously. The effect may also be torn down
    // mid-flight in development, where React runs it twice.
    let cancelled = false
    completeSignIn().then((outcome) => {
      if (cancelled || outcome.state === 'none') return
      if (outcome.state === 'failed') {
        setSignInError(outcome.reason)
        return
      }
      const next = {
        organizationSlug: identity?.organizationSlug ?? 'joes-auto',
        idToken: outcome.idToken,
      }
      setIdentity(next)
      saveIdentity(next)
    })
    return () => {
      cancelled = true
    }
  }, [identity?.organizationSlug])

  const me = useResource(api.me, identity)
  const services = useResource(api.services, identity)
  const appointments = useResource(api.appointments, identity)

  function signIn(persona: string) {
    const [slug] = persona.split('/')
    const next: Identity = { organizationSlug: slug ?? '', persona }
    setIdentity(next)
    saveIdentity(next)
  }

  function signOut() {
    setIdentity(null)
    saveIdentity(null)
  }

  if (!identity) {
    return (
      <main className="signin">
        <h1>3HAAI operations</h1>
        {signInError && (
          <p className="failed" role="alert">
            {signInError}
          </p>
        )}
        {hostedSignIn ? (
          <>
            <p className="note">
              Two pools, because staff and customers differ in the only way a pool cares about:
              a customer signs themselves up, a shop&apos;s staff are invited by that shop.
            </p>
            <ul className="personas">
              {availableAudiences().map((audience) => (
                <li key={audience}>
                  <button
                    className="button"
                    type="button"
                    onClick={() => {
                      setSignInError(null)
                      // `void` here swallowed every reason this could fail, so a browser with
                      // no Web Crypto — or any other refusal before the redirect — did nothing
                      // at all and said nothing about it.
                      beginSignIn(audience).catch((problem: unknown) =>
                        setSignInError(
                          problem instanceof Error ? problem.message : String(problem),
                        ),
                      )
                    }}
                  >
                    {audience === 'staff' ? 'Sign in as shop staff' : 'Sign in as a customer'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p className="note">
              No Cognito pool is configured, so this is the local development sign-in. It names a
              seeded person and proves nothing — the API refuses it unless{' '}
              <code>IDENTITY_MODE=dev_header</code>, which is never set on a deployed server.
            </p>
            <ul className="personas">
              {DEV_PERSONAS.map((p) => (
                <li key={p.value}>
                  <button onClick={() => signIn(p.value)}>{p.label}</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    )
  }

  const timezone = me.state === 'ready' ? me.value.timezone : 'UTC'

  return (
    <main>
      <header className="top">
        <h1>3HAAI</h1>
        {me.state === 'ready' && (
          <p className="who">
            <strong>{me.value.display_name}</strong>
            <span className="muted"> · {me.value.role ?? me.value.audience}</span>
            <span className="muted"> · {me.value.organization}</span>
          </p>
        )}
        <button className="link" onClick={signOut}>
          Sign out
        </button>
      </header>

      <Conversation identity={identity} />

      <Panel
        title="What this shop does"
        note="Read straight from the services table. No model was asked."
        resource={services}
      >
        {(list) => (
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Takes</th>
                <th className="right">Price</th>
              </tr>
            </thead>
            <tbody>
              {list.services.map((s: Service) => (
                <tr key={s.key}>
                  <td>{s.name}</td>
                  <td className="muted">{s.duration_minutes} min</td>
                  <td className="right">
                    <Money price={s.price} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title="Appointments"
        note="A customer sees only their own; staff see the shop's. The server decides which, from the database, on every request."
        resource={appointments}
      >
        {(list) =>
          list.appointments.length === 0 ? (
            <p className="muted">Nothing booked.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Service</th>
                  <th>With</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {list.appointments.map((a: Appointment) => (
                  <tr key={a.id}>
                    <td>
                      <When iso={a.starts_at} timezone={timezone} />
                    </td>
                    <td>{a.service_name}</td>
                    <td className="muted">{a.resource_name}</td>
                    <td>
                      <span className={`status ${a.status}`}>{a.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Panel>

      <Panel
        title="What you are allowed to do"
        note="Resolved from the database on every call. This list only hides buttons — the permission is checked again in the service behind each one."
        resource={me}
      >
        {(who) => (
          <ul className="permissions">
            {who.permissions.map((p) => (
              <li key={p}>
                <code>{p}</code>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </main>
  )
}
